import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    PortalContainer,
    SubTitle,
    Title,
    AccentButton,
    StyledInput,
} from '../../../styles/common';
import LoadingSpinner from '../../shared/LoadingSpinner';
import '../../../styles/fonts.css';

const LogoImage = styled.img`
    max-width: 350px;
    width: 100%;
    margin: 0 auto 2rem;
    display: block;
`;

const CenterContainer = styled.div`
    max-width: 600px;
    margin: 0 auto;
    text-align: left;
    padding: 0 1rem;

    @media (max-width: 576px) {
        padding: 0 0.5rem;
    }
`;

const InputWrapper = styled.div`
    display: flex;
    gap: 10px;
    align-items: flex-start;

    @media (max-width: 576px) {
        flex-direction: column;
        gap: 20px;
    }
`;

const UsernameInput = styled(StyledInput)`
    flex: 1;
    width: 100% !important;
`;

const VerifyButton = styled(AccentButton)`
    margin-top: 0;
    min-width: 100px;
    white-space: nowrap;

    @media (max-width: 576px) {
        width: 50%;
        min-width: unset;
        align-self: center;
    }
`;

const ErrorBox = styled.div`
    padding: 16px;
    background-color: #fcd4ca;
    border-radius: 24px;
    margin-top: 15px;
`;

const GenericMessage = styled.div`
    font-style: italic;
    padding: 16px;
    background-color: #d4f4dd;
    border-radius: 24px;
    margin-top: 15px;
`;

const Recovery = () => {
    const { t } = useTranslation();
    const [username, setUsername] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showMessage, setShowMessage] = useState(false);
    const [error, setError] = useState('');
    const [token, setToken] = useState(null);
    const [tokenLoading, setTokenLoading] = useState(true);

    // Fetch token on component mount
    const fetchToken = async () => {
        try {
            const response = await fetch('/user/api/auth/confirm-token', {
                method: 'GET',
                credentials: 'omit',
            });

            if (response.ok) {
                const tokenJson = await response.json();
                return { token: tokenJson.token, error: null };
            } else {
                return { token: null, error: t('recovery$error$tokenFailed') };
            }
        } catch (err) {
            return { token: null, error: t('recovery$error$tokenFailed') };
        }
    };

    useEffect(() => {
        const initToken = async () => {
            const { token: newToken, error: tokenError } = await fetchToken();
            if (newToken) {
                setToken(newToken);
            } else if (tokenError) {
                setError(tokenError);
            }
            setTokenLoading(false);
        };

        initToken();
    }, [t]);

    const handleUsernameChange = (event) => {
        setUsername(event.target.value);
        setError('');
        setShowMessage(false);
    };

    const handleVerify = async () => {
        if (!username.trim()) {
            setError(t('recovery$error$emptyUsername'));
            return;
        }

        setIsLoading(true);
        setError('');
        setShowMessage(false);

        try {
            const { token: validToken, error: tokenError } = await fetchToken();

            if (tokenError) {
                setError(tokenError);
                setIsLoading(false);
                return;
            }

            if (validToken !== token) {
                setToken(validToken);
            }

            // Self-service verification with JWT token
            const headers = {
                'Content-Type': 'application/json',
            };

            // Add token if available (JWT filter expects just the token, not "Bearer token")
            if (validToken) {
                headers.Authorization = validToken;
            }

            const response = await fetch(
                `/user/api/user/${username.trim()}/verify`,
                {
                    method: 'POST',
                    credentials: 'omit',
                    headers,
                    body: JSON.stringify({
                        verificationType: 'EMAIL',
                        isSelfService: true,
                    }),
                },
            );

            setShowMessage(true);
        } catch (err) {
            console.error('Verification error:', err);
            setShowMessage(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && username.trim() && !isLoading) {
            handleVerify();
        }
    };

    // Show loading spinner while fetching token
    if (tokenLoading) {
        return (
            <PortalContainer>
                <LoadingSpinner />
            </PortalContainer>
        );
    }

    return (
        <PortalContainer>
            <LogoImage
                src="/user/static/branding/images/Axiad-Confirm-Horizontal.png"
                alt="Axiad Logo"
            />

            <CenterContainer>
                <Title>{t('recovery$title')}</Title>
                <SubTitle>{t('recovery$subtitle')}</SubTitle>

                <InputWrapper>
                    <UsernameInput
                        id="username-input"
                        type="text"
                        placeholder={t('recovery$placeholder$username')}
                        value={username}
                        onChange={handleUsernameChange}
                        disabled={isLoading}
                        onKeyPress={handleKeyPress}
                    />
                    <VerifyButton
                        outline
                        size="sm"
                        color="primary"
                        onClick={handleVerify}
                        disabled={!username.trim() || isLoading}
                        aria-disabled={!username.trim() || isLoading}
                    >
                        {isLoading
                            ? t('recovery$button$verifying')
                            : t('recovery$button$verify')}
                    </VerifyButton>
                </InputWrapper>

                {error && <ErrorBox>{error}</ErrorBox>}
                {showMessage && (
                    <GenericMessage>
                        {t('recovery$message$generic')}
                    </GenericMessage>
                )}
            </CenterContainer>

            {isLoading && <LoadingSpinner />}
        </PortalContainer>
    );
};

export default Recovery;
